import { createApiRef, createApiFactory, githubAuthApiRef, gitlabAuthApiRef, microsoftAuthApiRef, bitbucketAuthApiRef, useApp } from '@backstage/core-plugin-api';
import { ScmIntegrations } from '@backstage/integration';
import CodeIcon from '@material-ui/icons/Code';
import React from 'react';

const scmAuthApiRef = createApiRef({
  id: "core.scmauth"
});

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _providers, _api, _host, _scopeMapping, _providerName;
class ScmAuthMux {
  constructor(providers) {
    __privateAdd(this, _providers, void 0);
    __privateSet(this, _providers, providers);
  }
  async getCredentials(options) {
    const url = new URL(options.url);
    const provider = __privateGet(this, _providers).find((p) => p.isUrlSupported(url));
    if (!provider) {
      throw new Error(
        `No auth provider available for '${options.url}', see https://backstage.io/link?scm-auth`
      );
    }
    return provider.getCredentials(options);
  }
}
_providers = new WeakMap();
const _ScmAuth = class _ScmAuth {
  constructor(providerName, api, host, scopeMapping) {
    __privateAdd(this, _api, void 0);
    __privateAdd(this, _host, void 0);
    __privateAdd(this, _scopeMapping, void 0);
    __privateAdd(this, _providerName, void 0);
    __privateSet(this, _api, api);
    __privateSet(this, _host, host);
    __privateSet(this, _scopeMapping, scopeMapping);
    __privateSet(this, _providerName, providerName);
  }
  /**
   * Creates an API factory that enables auth for each of the default SCM providers.
   */
  static createDefaultApiFactory() {
    return createApiFactory({
      api: scmAuthApiRef,
      deps: {
        github: githubAuthApiRef,
        gitlab: gitlabAuthApiRef,
        azure: microsoftAuthApiRef,
        bitbucket: bitbucketAuthApiRef
      },
      factory: ({ github, gitlab, azure, bitbucket }) => _ScmAuth.merge(
        _ScmAuth.forGithub(github),
        _ScmAuth.forGitlab(gitlab),
        _ScmAuth.forAzure(azure),
        _ScmAuth.forBitbucket(bitbucket)
      )
    });
  }
  /**
   * Creates a general purpose ScmAuth instance with a custom scope mapping.
   */
  static forAuthApi(authApi, options) {
    return new _ScmAuth("generic", authApi, options.host, options.scopeMapping);
  }
  /**
   * Creates a new ScmAuth instance that handles authentication towards GitHub.
   *
   * The host option determines which URLs that are handled by this instance and defaults to `github.com`.
   *
   * The default scopes are:
   *
   * `repo read:org read:user`
   *
   * If the additional `repoWrite` permission is requested, these scopes are added:
   *
   * `gist`
   */
  static forGithub(githubAuthApi, options) {
    var _a;
    const host = (_a = options == null ? void 0 : options.host) != null ? _a : "github.com";
    return new _ScmAuth("github", githubAuthApi, host, {
      default: ["repo", "read:org", "read:user"],
      repoWrite: ["gist"]
    });
  }
  /**
   * Creates a new ScmAuth instance that handles authentication towards GitLab.
   *
   * The host option determines which URLs that are handled by this instance and defaults to `gitlab.com`.
   *
   * The default scopes are:
   *
   * `read_user read_api read_repository`
   *
   * If the additional `repoWrite` permission is requested, these scopes are added:
   *
   * `write_repository api`
   */
  static forGitlab(gitlabAuthApi, options) {
    var _a;
    const host = (_a = options == null ? void 0 : options.host) != null ? _a : "gitlab.com";
    return new _ScmAuth("gitlab", gitlabAuthApi, host, {
      default: ["read_user", "read_api", "read_repository"],
      repoWrite: ["write_repository", "api"]
    });
  }
  /**
   * Creates a new ScmAuth instance that handles authentication towards Azure.
   *
   * The host option determines which URLs that are handled by this instance and defaults to `dev.azure.com`.
   *
   * The default scopes are:
   *
   * `vso.build vso.code vso.graph vso.project vso.profile`
   *
   * If the additional `repoWrite` permission is requested, these scopes are added:
   *
   * `vso.code_manage`
   */
  static forAzure(microsoftAuthApi, options) {
    var _a;
    const host = (_a = options == null ? void 0 : options.host) != null ? _a : "dev.azure.com";
    return new _ScmAuth("azure", microsoftAuthApi, host, {
      default: [
        "vso.build",
        "vso.code",
        "vso.graph",
        "vso.project",
        "vso.profile"
      ],
      repoWrite: ["vso.code_manage"]
    });
  }
  /**
   * Creates a new ScmAuth instance that handles authentication towards Bitbucket.
   *
   * The host option determines which URLs that are handled by this instance and defaults to `bitbucket.org`.
   *
   * The default scopes are:
   *
   * `account team pullrequest snippet issue`
   *
   * If the additional `repoWrite` permission is requested, these scopes are added:
   *
   * `pullrequest:write snippet:write issue:write`
   */
  static forBitbucket(bitbucketAuthApi, options) {
    var _a;
    const host = (_a = options == null ? void 0 : options.host) != null ? _a : "bitbucket.org";
    return new _ScmAuth("bitbucket", bitbucketAuthApi, host, {
      default: ["account", "team", "pullrequest", "snippet", "issue"],
      repoWrite: ["pullrequest:write", "snippet:write", "issue:write"]
    });
  }
  /**
   * Merges together multiple ScmAuth instances into one that
   * routes requests to the correct instance based on the URL.
   */
  static merge(...providers) {
    return new ScmAuthMux(providers);
  }
  /**
   * Checks whether the implementation is able to provide authentication for the given URL.
   */
  isUrlSupported(url) {
    return url.host === __privateGet(this, _host);
  }
  getAdditionalScopesForProvider(additionalScopes) {
    var _a, _b;
    if (!(additionalScopes == null ? void 0 : additionalScopes.customScopes) || __privateGet(this, _providerName) === "generic") {
      return [];
    }
    return (_b = (_a = additionalScopes.customScopes) == null ? void 0 : _a[__privateGet(this, _providerName)]) != null ? _b : [];
  }
  /**
   * Fetches credentials for the given resource.
   */
  async getCredentials(options) {
    const { url, additionalScope, ...restOptions } = options;
    const scopes = __privateGet(this, _scopeMapping).default.slice();
    if (additionalScope == null ? void 0 : additionalScope.repoWrite) {
      scopes.push(...__privateGet(this, _scopeMapping).repoWrite);
    }
    const additionalScopes = this.getAdditionalScopesForProvider(additionalScope);
    if (additionalScopes.length) {
      scopes.push(...additionalScopes);
    }
    const uniqueScopes = [...new Set(scopes)];
    const token = await __privateGet(this, _api).getAccessToken(uniqueScopes, restOptions);
    return {
      token,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  }
};
_api = new WeakMap();
_host = new WeakMap();
_scopeMapping = new WeakMap();
_providerName = new WeakMap();
let ScmAuth = _ScmAuth;

class ScmIntegrationsApi {
  /**
   * Instantiates an {@link @backstage/integration#ScmIntegrationRegistry}.
   *
   * @param config - The root of the config hierarchy.
   */
  static fromConfig(config) {
    return ScmIntegrations.fromConfig(config);
  }
}
const scmIntegrationsApiRef = createApiRef({
  id: "integration.scmintegrations"
});

const ScmIntegrationIcon = (props) => {
  var _a;
  const { type } = props;
  const app = useApp();
  const DefaultIcon = CodeIcon;
  const Icon = type ? (_a = app.getSystemIcon(type)) != null ? _a : DefaultIcon : DefaultIcon;
  return /* @__PURE__ */ React.createElement(Icon, null);
};

export { ScmAuth, ScmIntegrationIcon, ScmIntegrationsApi, scmAuthApiRef, scmIntegrationsApiRef };
//# sourceMappingURL=index.esm.js.map
